/**
 * SG — AGM with Unaudited Financial Statements
 *
 * 폼 모델 / 빈 모델 팩토리 / 템플릿 변수 변환 / 태스크 formData 빌드 / 검증.
 *
 * 7개 문서 전체 변수 명세 (최종본):
 *
 *  1. Attendance List.docx
 *     company_name, company_registration_no, agm_venue, agm_date, agm_time,
 *     is_multiple_members, member.name(loop),
 *     is_sole_corporate_member, member_name, member_representative_name,
 *     is_sole_individual_member, member_name, Signers
 *
 *  2. Consent to Short Notice.docx
 *     … Signers: multiple=주주 전원, sole_corporate=대표, sole_individual=단독 주주
 *
 *  3. Directors' Resolutions for AGM (Approval of UFS).docx
 *     company_name, company_registration_no, financial_year_end_date,
 *     is_part_of_group, director_count, specify_signers,
 *     designated_signers(loop: signer_name),
 *     agm_venue, agm_time, is_short_notice,
 *     has_directors_remuneration, has_retiring_directors, director.name(loop),
 *     resolution_date, Signers
 *
 *  4. Minutes of AGM (Multiple).docx
 *     company_name, company_registration_no,
 *     agm_date, agm_time, agm_venue, chairman_name,
 *     is_short_notice, financial_year_end_date,
 *     has_directors_remuneration, has_retiring_directors, Signers
 *
 *  5. Minutes of AGM (Sole Corporate).docx
 *     company_name, company_registration_no,
 *     member_representative_name, member_name, agm_date,
 *     financial_year_end_date,
 *     has_directors_remuneration, has_retiring_directors, Signers
 *
 *  6. Minutes of AGM (Sole Individual).docx
 *     company_name, company_registration_no,
 *     member_name, agm_date, financial_year_end_date,
 *     has_directors_remuneration, has_retiring_directors, Signers
 *
 *  7. Notice of AGM.docx
 *     company_name, company_registration_no, registered_office_address,
 *     agm_venue, agm_date, agm_time, financial_year_end_date,
 *     has_retiring_directors, has_directors_remuneration,
 *     notice_date, Signers
 */

import { dedupePresetSigners } from '../../../presetSignerDedup';

/* ---------- repeating-section 타입 ---------- */
export interface MemberRow {
  name: string;
  address: string;
  contactCd?: string;
}

export interface RetiringDirectorRow {
  name: string;
}

export interface SignerRow {
  name: string;
}

/** 엔티티 조회 시 스냅샷 — Attendance List 서명자 자동 지정·CONTACT_REG_NO 중복 제거용 */
export interface AgmEntityPersonRow {
  name: string;
  contactRegNo?: string;
  contactCd?: string;
  email?: string;
  role?: 'director' | 'shareholder';
}

/* ---------- 폼 모델 ---------- */
export interface AgmUfsFormModel {
  /* Company */
  companyName: string;
  companyRegistrationNo: string;
  registeredOfficeAddress: string;

  /* AGM schedule */
  agmVenue: string;
  agmDate: string;
  agmTime: string;

  /* Financial year */
  financialYearEndDate: string;

  /* Conditions */
  isPartOfGroup: boolean;
  isShortNotice: boolean;
  hasDirectorsRemuneration: boolean;
  hasRetiringDirectors: boolean;

  /* Member type — exactly one of three */
  memberType: 'multiple' | 'sole_corporate' | 'sole_individual';

  /* Members (for multiple) */
  members: MemberRow[];

  /* Sole corporate / individual member */
  memberName: string;
  memberRepresentativeName: string;

  /* Chairman (for multiple-member minutes) */
  chairmanName: string;

  /* Directors */
  directorCount: number;
  specifySigners: boolean;
  signers: SignerRow[];

  /* Retiring directors */
  retiringDirectors: RetiringDirectorRow[];

  /* ── Signers: Attendance → buildAgmUfsAttendanceSignerVariables; Consent → buildAgmUfsConsentSignerVariables ── */
  entityDirectors: AgmEntityPersonRow[];
  entityMembers: AgmEntityPersonRow[];

  /* Dates */
  noticeDate: string;
  consentDate: string;
  resolutionDate: string;
}

export function createEmptyAgmUfsForm(): AgmUfsFormModel {
  return {
    companyName: '',
    companyRegistrationNo: '',
    registeredOfficeAddress: '',
    agmVenue: '',
    agmDate: '',
    agmTime: '',
    financialYearEndDate: '',
    isPartOfGroup: false,
    isShortNotice: false,
    hasDirectorsRemuneration: false,
    hasRetiringDirectors: false,
    memberType: 'multiple',
    members: [],
    memberName: '',
    memberRepresentativeName: '',
    chairmanName: '',
    directorCount: 1,
    specifySigners: false,
    signers: [],
    retiringDirectors: [],
    entityDirectors: [],
    entityMembers: [],
    noticeDate: '',
    consentDate: '',
    resolutionDate: '',
  };
}

/* ---------- helpers ---------- */
function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/* ---------- template variables ---------- */

/**
 * 폼 모델 → 문서 템플릿 변수 변환.
 * 모든 7개 문서에서 사용되는 [[variable]] 을 누락 없이 매핑.
 */
export function toAgmUfsTemplateVariables(m: AgmUfsFormModel): Record<string, any> {
  const isMultiple = m.memberType === 'multiple';
  const isSoleCorporate = m.memberType === 'sole_corporate';
  const isSoleIndividual = m.memberType === 'sole_individual';

  /* ── loop: members (Attendance List — [[member.name]]) ── */
  const members = (m.members || []).map((r) => ({
    member_name: trim(r.name),
    member_address: trim(r.address),
  }));

  return {
    /* ── ALL 7 docs ── */
    company_name: trim(m.companyName),
    company_registration_no: trim(m.companyRegistrationNo),

    /* ── Consent, Notice ── */
    registered_office_address: trim(m.registeredOfficeAddress),

    /* ── Attendance, Consent, DR, Minutes(Multiple), Notice ── */
    agm_venue: trim(m.agmVenue),
    agm_date: trim(m.agmDate),
    agm_time: trim(m.agmTime),

    /* ── DR, Minutes(all 3), Notice ── */
    financial_year_end_date: trim(m.financialYearEndDate),

    /* ── DR only ── */
    is_part_of_group: m.isPartOfGroup,

    /* ── DR, Minutes(Multiple) ── */
    is_short_notice: m.isShortNotice,

    /* ── DR, Minutes(all 3), Notice ── */
    has_directors_remuneration: m.hasDirectorsRemuneration,
    has_retiring_directors: m.hasRetiringDirectors,

    /* ── Attendance, Consent ── */
    is_multiple_members: isMultiple,
    is_sole_corporate_member: isSoleCorporate,
    is_sole_individual_member: isSoleIndividual,

    /* ── Attendance(loop) ── */
    members,

    /* ── Attendance, Consent, Minutes(Sole Corp), Minutes(Sole Indiv) ── */
    member_name: isSoleCorporate || isSoleIndividual ? trim(m.memberName) : '',

    /* ── Attendance, Consent, Minutes(Sole Corp) ── */
    member_representative_name: isSoleCorporate ? trim(m.memberRepresentativeName) : '',

    /* ── Minutes(Multiple) ── */
    chairman_name: trim(m.chairmanName),

    /* ── DR ── */
    director_count: m.directorCount,
    'director_count == 1': m.directorCount === 1,
    'director_count == 2': m.directorCount === 2,
    specify_signers: m.specifySigners,

    /* ── Notice ── */
    notice_date: trim(m.noticeDate),

    /* ── Consent ── */
    consent_date: trim(m.consentDate),

    /* ── DR ── */
    resolution_date: trim(m.resolutionDate),
  };
}

/** DR — [[if retiring_directors]] … [[director.name]] … [[/if retiring_directors]] */
export function toAgmUfsRetiringDirectorsRepeatingData(m: AgmUfsFormModel): Array<Record<string, string>> {
  return (m.retiringDirectors || [])
    .map((r) => {
      const name = trim(r.name);
      return { 'director.name': name, director_name: name };
    })
    .filter((row) => row['director.name']);
}

/**
 * Directors' Resolutions for AGM — DR 전용 변수 (서명자·퇴임 이사 재선임).
 */
export function toAgmUfsDirectorsResolutionOnlyVariables(m: AgmUfsFormModel): Record<string, any> {
  return {
    ...toAgmUfsDirectorsResolutionSignerVariables(m),
    retiring_directors: toAgmUfsRetiringDirectorsRepeatingData(m),
  };
}

/**
 * @deprecated Use toAgmUfsDirectorsResolutionOnlyVariables
 */
export function toAgmUfsDirectorsResolutionSignerVariables(m: AgmUfsFormModel): Record<string, any> {
  const signers_list = (m.signers || []).map((s) => ({
    signer_name: trim(s.name),
  }));
  const deduped = dedupePresetSigners(
    signers_list
      .map((s) => ({
        name: trim(s.signer_name),
        email: '',
        role: 'director',
      }))
      .filter((s) => s.name),
    { chairmanName: trim(m.chairmanName) },
  );
  const signersJson = deduped.length ? JSON.stringify(deduped) : '';
  return {
    /* Word: [[if designated_signers]] … [[signer_name]] … [[/if designated_signers]] */
    designated_signers: signers_list,
    signers_list,
    signers: signersJson,
    Signers: signersJson,
  };
}

/* ---------- task form data (L_TASK_DATA.formData) ---------- */
export function buildAgmUfsTaskFormData(
  m: AgmUfsFormModel,
  _entityCd: string,
): Record<string, any> {
  return {
    companyName: trim(m.companyName),
    companyRegistrationNo: trim(m.companyRegistrationNo),
    registeredOfficeAddress: trim(m.registeredOfficeAddress),
    agmVenue: trim(m.agmVenue),
    agmDate: trim(m.agmDate),
    agmTime: trim(m.agmTime),
    financialYearEndDate: trim(m.financialYearEndDate),
    memberType: m.memberType,
    memberName: trim(m.memberName),
    memberRepresentativeName: trim(m.memberRepresentativeName),
    chairmanName: trim(m.chairmanName),
    isPartOfGroup: m.isPartOfGroup,
    isShortNotice: m.isShortNotice,
    hasDirectorsRemuneration: m.hasDirectorsRemuneration,
    hasRetiringDirectors: m.hasRetiringDirectors,
    directorCount: m.directorCount,
    specifySigners: m.specifySigners,
    signers: (m.signers || []).map((s) => ({ name: trim(s.name) })),
    retiringDirectors: (m.retiringDirectors || []).map((r) => ({ name: trim(r.name) })),
    members: (m.members || []).map((r) => ({
      name: trim(r.name),
      address: trim(r.address),
      contactCd: trim(r.contactCd),
    })),
    noticeDate: trim(m.noticeDate),
    consentDate: trim(m.consentDate),
    resolutionDate: trim(m.resolutionDate),
  };
}

/* ---------- validation ---------- */
export function isAgmUfsFormComplete(m: AgmUfsFormModel): boolean {
  const base = [
    m.companyName,
    m.companyRegistrationNo,
    m.agmVenue,
    m.agmDate,
    m.agmTime,
    m.financialYearEndDate,
    m.noticeDate,
  ].every((v) => trim(v).length > 0);

  if (!base) return false;

  if (m.memberType === 'multiple') {
    if (m.members.length < 2) return false;
    if (!m.members.every((r) => trim(r.name))) return false;
    if (!trim(m.chairmanName)) return false;
  } else if (m.memberType === 'sole_corporate') {
    if (!trim(m.memberName) || !trim(m.memberRepresentativeName)) return false;
  } else if (m.memberType === 'sole_individual') {
    if (!trim(m.memberName)) return false;
  }

  if (m.hasRetiringDirectors) {
    if (m.retiringDirectors.length === 0) return false;
    if (!m.retiringDirectors.every((r) => trim(r.name))) return false;
  }
  if (m.specifySigners && !m.signers.some((s) => trim(s.name))) return false;
  if (m.isShortNotice && !trim(m.consentDate)) return false;

  return true;
}
